import { useEffect, useRef } from "react";

/**
 * Fond animé de l'en-tête de la page Analyses : un réseau de points violets
 * qui dérivent lentement et se relient quand ils se rapprochent.
 * - occupe la partie droite et s'estompe vers la gauche (la recherche et les filtres restent lisibles)
 * - image fixe si l'utilisateur a demandé moins d'animations (prefers-reduced-motion)
 * Le parent doit être en `position: relative` (et idéalement `overflow-hidden`).
 */
const COLOR = "177,117,255"; // violet-400 de la palette Side-B
const LINK_DIST = 150; // distance max (px) pour relier deux points
const SPEED = 12; // vitesse max d'un point (px / seconde)
const MIN_X = 0.35; // les points restent dans les 65 % de droite

export default function NetworkBackground({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let points = [];
    let minX = MIN_X;
    let maxY = 1;
    let last = 0;
    let frame = 0;

    const setup = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Téléphone : le réseau reste dans le coin en haut à droite (la zone visible), avec assez de points
      const small = width < 640;
      minX = small ? 0.42 : MIN_X;
      maxY = small ? 0.62 : 1;
      const count = small ? 16 : Math.round((width * height) / 11000);
      points = Array.from({ length: count }, () => ({
        x: width * (minX + Math.random() * (1 - minX)),
        y: Math.random() * height * maxY,
        vx: (Math.random() - 0.5) * SPEED,
        vy: (Math.random() - 0.5) * SPEED,
      }));
    };

    const draw = (dt) => {
      ctx.clearRect(0, 0, width, height);
      points.forEach((p) => {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        if (p.x < width * minX || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height * maxY) p.vy *= -1;
      });
      for (let i = 0; i < points.length; i += 1) {
        for (let j = i + 1; j < points.length; j += 1) {
          const d = Math.hypot(points[i].x - points[j].x, points[i].y - points[j].y);
          if (d < LINK_DIST) {
            ctx.strokeStyle = `rgba(${COLOR},${(1 - d / LINK_DIST) * 0.45})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(points[i].x, points[i].y);
            ctx.lineTo(points[j].x, points[j].y);
            ctx.stroke();
          }
        }
      }
      ctx.fillStyle = `rgba(${COLOR},0.8)`;
      points.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const tick = (now) => {
      const dt = Math.min(0.1, (now - (last || now)) / 1000);
      last = now;
      draw(dt);
      frame = requestAnimationFrame(tick);
    };

    setup();
    draw(0);
    if (!reduceMotion) frame = requestAnimationFrame(tick);

    const observer = new ResizeObserver(() => {
      setup();
      draw(0);
    });
    observer.observe(canvas);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const mask = "linear-gradient(90deg, transparent 0%, transparent 58%, #000 85%)";
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`hero-anim absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{ WebkitMaskImage: mask, maskImage: mask }}
      data-testid="analyses-network-bg"
    />
  );
}
